// js/AnimationManager.js
import AccessibilityManager from './core/AccessibilityManager.js'; // Import dependency

export default class AnimationManager { // Add export default
  /**
   * @param {AccessibilityManager} accessibilityManager
   */
  constructor(accessibilityManager) {
    if (!(accessibilityManager instanceof AccessibilityManager)) {
      console.warn("AnimationManager: AccessibilityManager instance not provided correctly.");
      // Create a dummy instance or throw error depending on strictness
      this.accessibilityManager = { isReducedMotionMode: false, loadSettings: () => {}, applySettings: () => {}, announce: () => {} };
    } else {
      this.accessibilityManager = accessibilityManager;
    }
    this.fireworksContainer = document.getElementById('fireworks-container');
    this.fireworkSound = document.getElementById('firework-sound'); // Reference to audio element

    this._addBaseStyles();
  }

  /**
   * Add necessary CSS keyframes and base styles dynamically.
   */
  _addBaseStyles() {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = `
      @keyframes green-tile-flash {
        0% { transform: scale(1); }
        50% { transform: scale(1.2); background-color: #00FF00 !important; box-shadow: 0 0 20px #00FF00; }
        100% { transform: scale(1); }
      }

      .firework {
        position: absolute;
        width: 5px;
        height: 5px;
        border-radius: 50%;
        transform: scale(0);
        opacity: 1;
        transition: transform 0.8s ease-out, opacity 0.8s ease-out;
        pointer-events: none;
        z-index: 120;
        box-shadow: 0 0 10px 2px currentColor; /* Use currentColor for dynamic color */
      }

      .confetti {
        position: absolute;
        width: 10px;
        height: 10px;
        pointer-events: none;
        z-index: 110;
        animation: fall 4s ease-out forwards;
        opacity: 0.8;
      }

      @keyframes fall {
        0% { transform: translateY(-20px) rotate(0deg); opacity: 0.8; }
        100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
      }

      @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.7; }
          100% { opacity: 1; }
      }
    `;
    document.head.appendChild(styleSheet);
  }

  /**
   * Animate tiles that have just turned green.
   * @param {Array<Array<number>>} oldState - Previous validation state grid
   * @param {Array<Array<number>>} newState - Current validation state grid
   * @param {number} gridSize - The size of the grid.
   */
  animateNewlyGreenTiles(oldState, newState, gridSize) {
    if (this.accessibilityManager && this.accessibilityManager.isReducedMotionMode) {
      console.log('Animation skipped - reduced motion enabled');
      return;
    }

    if (!oldState || !newState) {
        console.warn('Cannot animate green tiles: old or new state is missing.');
        return;
    }

    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        // Check if the tile was not green before but is green now
        if (oldState[row][col] !== 4 && newState[row][col] === 4) {
          const tile = document.querySelector(`.tile[data-row="${row}"][data-col="${col}"]`);
          if (tile) {
            console.log(`Animating tile at ${row},${col}`);
            tile.style.animation = 'green-tile-flash 0.6s ease-in-out';
            tile.style.position = 'relative'; // Needed for z-index
            tile.style.zIndex = '100';

            // Remove animation after it completes
            setTimeout(() => {
              if (tile) { // Check if tile still exists
                tile.style.animation = '';
                tile.style.zIndex = '';
                tile.style.position = '';
              }
            }, 700); // Slightly longer than animation duration
          }
        }
      }
    }
  }

  /**
   * Create fireworks animation for victory celebration.
   * @param {boolean} isHardMode - If the victory was in hard mode (for intensity).
   */
  createFireworks(isHardMode = false) {
     if (this.accessibilityManager && this.accessibilityManager.isReducedMotionMode) {
      console.log('Fireworks skipped - reduced motion enabled');
      return;
    }

    if (!this.fireworksContainer) {
        console.error("Fireworks container not found!");
        return;
    }
    this.fireworksContainer.innerHTML = ''; // Clear previous effects

    const numFireworks = isHardMode ? 50 : 30;

    for (let i = 0; i < numFireworks; i++) {
      setTimeout(() => {
        if (!this.fireworksContainer) return; // Check if container still exists

        const firework = document.createElement('div');
        firework.className = 'firework';

        const left = Math.random() * 100;
        const top = Math.random() * 100;
        const colors = [
          '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
          '#FF4500', '#7CFC00', '#1E90FF', '#FFD700', '#FF1493', '#00FFFF'
        ];
        const color = colors[Math.floor(Math.random() * colors.length)];

        firework.style.left = `${left}%`;
        firework.style.top = `${top}%`;
        firework.style.backgroundColor = color;
        // firework.style.boxShadow = `0 0 10px 2px ${color}`; // Handled by CSS using currentColor

        this.fireworksContainer.appendChild(firework);

        // Animate expansion using requestAnimationFrame for smoother start
        requestAnimationFrame(() => {
            firework.style.transform = 'scale(30)';
            firework.style.opacity = '0';
        });


        // Remove after animation
        setTimeout(() => {
          firework.remove();
        }, 1000); // Matches transition duration

      }, i * 100 + Math.random() * 500); // Randomized timing
    }

    // Create confetti
    this.createConfetti();

  }

  /**
   * Create confetti animation.
   */
  createConfetti() {
    if (this.accessibilityManager && this.accessibilityManager.isReducedMotionMode) {
      console.log('Confetti skipped - reduced motion enabled');
      return;
    }
     if (!this.fireworksContainer) return;

    const numConfetti = 100;
    const colors = ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800', '#FF5722'];
    const shapes = ['square', 'circle', 'triangle', 'rect', 'diamond'];

    for (let i = 0; i < numConfetti; i++) {
      setTimeout(() => {
         if (!this.fireworksContainer) return;

        const confetti = document.createElement('div');
        confetti.className = 'confetti';

        const left = Math.random() * 100;
        const color = colors[Math.floor(Math.random() * colors.length)];
        const shape = shapes[Math.floor(Math.random() * shapes.length)];

        confetti.style.left = `${left}%`;
        confetti.style.top = '-20px'; // Start above the screen
        confetti.style.backgroundColor = color;
        confetti.style.animationDuration = `${3 + Math.random() * 3}s`;

        if (shape === 'circle') {
          confetti.style.borderRadius = '50%';
        } else if (shape === 'triangle') {
          confetti.style.width = '0';
          confetti.style.height = '0';
          confetti.style.backgroundColor = 'transparent';
          confetti.style.borderLeft = '5px solid transparent';
          confetti.style.borderRight = '5px solid transparent';
          confetti.style.borderBottom = `10px solid ${color}`;
        } else if (shape === 'rect') {
          confetti.style.width = '5px';
          confetti.style.height = '15px';
        } else if (shape === 'diamond') {
          confetti.style.width = '10px'; // Ensure diamond has size before rotate
          confetti.style.height = '10px';
          confetti.style.transform = 'rotate(45deg)';
        } else { // Default to square
            confetti.style.width = '10px';
            confetti.style.height = '10px';
        }


        this.fireworksContainer.appendChild(confetti);

        // Remove after animation
        setTimeout(() => {
          confetti.remove();
        }, 6000); // Longer than max animation duration

      }, i * 50); // Staggered creation
    }
  }
}
// Removed window export
